"use strict";

function QuoteRequestRepository(Util,
	storage,
	quoteOptions,
	quoteConfiguration,
	userInputRepository,
	serviceResponseRepository,
	structuredRequestTransformer,
	defaultValueRequestTransformer,
	formatConverterTransformer,
	cleanRequestTransformer,
	splitterRequestTransformer,
	publicIDTransformer,
	policyHolderHandler,
	coverTypeTransformer,
	coverTypeResolver,
	coverOfferingResolver,
	triggerGoalTransformer
) {

	var userInput = null;

	var lastQuoteResponse = null;

	function getUserInput() {
		return userInput;
	}

	function updateQuoteRequest(input) {
		userInput = Util.deepClone(input);
	}

	function getLastRequest() {
		return quoteRequest;
	}

	function forgePublicId(moduleName) {
		return Util.stringFormat('portal:{0}-{1}', moduleName, Util.generateRandomString(15));
	}

	var moduleNameMapping = {
		"vehiclesDriver": 'QPMVehicleDriver',
		"vehiclesPolicyDriver": 'QPMPolicyDriver',
		"vehiclesPerson": 'person',
		"vehiclesDriverHistory": "QPMDriverHistory",
		"caravansDriver": "QPCVehicleDriver",
		"caravansPolicyDriver": "QPCPolicyDriver",
		"caravansPerson": "Person",
		"caravansDriverHistory": "QPCDriverHistory",
		"motorcyclesDriver": 'QPYMotorcycleRider',
		"motorcyclesPolicyDriver": 'QPYPolicyRider',
		"motorcyclesPerson": 'Person',
		"motorcyclesDriverHistory": "QPYRiderHistory",
	};

	/**
	 * Generates the json for the body of the QuickQuote request, by applying
	 * quote specific transformations (based on the corresponding response received from the service).
	 * @quotePermutations: a list of coverType, paymentFrequency and quoteType permutations to generate the final request for.
	 */
	function splitQuotes(quotePermutations) {
		var quoteRequest = transformQuickQuoteRequest();
		if (!quotePermutations.offerings)
			quotePermutations.offerings = [quoteOptions.getSelectedOffering()];

		var quotes = splitterRequestTransformer.transform(quotePermutations, quoteRequest);
		return quotes;
	}

	function getQuoteTriggerRequest() {
		var quoteRequest = transformQuickQuoteRequest();
		var request = triggerGoalTransformer.transform(quoteRequest);
		return request;
	}

	function transformQuickQuoteRequest() {
		var input = Util.deepClone(userInput);
		var structuredRequest = structuredRequestTransformer.transform(input);
		var valueSubbedRequest = defaultValueRequestTransformer.transform(structuredRequest, input);
		var valueReformattedRequest = formatConverterTransformer.transform(valueSubbedRequest);
		var cleanedRequest = cleanRequestTransformer.transform(valueReformattedRequest);
		return cleanedRequest;
	}

	/**
	 * Generate the json body of a Full Quote Request.  This body is used for both PUT and POST requests
	 * and sent to the guideWire service.
	 */
	function getSaveQuoteRequest(paymentFrequency) {
		var quoteRequest = transformSaveQuoteRequest();
		var q = Util.deepClone(quoteRequest);
		q = Object.extend(q, {
			quoteType: 'Full',
			paymentFrequency: paymentFrequency
		});

		//added firstAnchorDate and firstDayOfMonth for monthly and fortnightly payments
		if (quoteRequest.paymentForm && quoteRequest.paymentForm.paymentDetails && quoteRequest.paymentForm.paymentDetails.paymentStartDate) {
			var dateString = quoteRequest.paymentForm.paymentDetails.paymentStartDate;
			q.firstAnchorDate = moment(dateString, 'DD/MM/YYYY').toISOString();
			q.firstDayOfMonth = moment(dateString, 'DD/MM/YYYY').format('D');
		}
		var coverType = quoteOptions.getSelectedCoverType();
		var offering = quoteOptions.getSelectedOffering(),
			offeringDescriptor = offering ? offering.descriptor : null;;

		q = coverTypeTransformer.transform(q, coverType.descriptor, offeringDescriptor);

		var cred = getSelectedQuoteCredentials();
		if (cred) {
			q.quoteID = cred.quoteID;
			q.secret = cred.secret;
		}

		var lastResponse = findMatchingQuoteResponse(coverType, offering);

		//check from last response if added driver has excluded usage
		if (lastResponse !== null) {
			var productType = null;

			if (angular.isObject(lastResponse.vehicles)) productType = 'vehicles';

			if (angular.isObject(lastResponse.caravans)) productType = 'caravans';

			//applicable for motor products
			if (productType != null) {
				angular.forEach(lastResponse[productType][0].drivers, function (driver, index) {
					if (driver.driverUsage === 'excluded') {
						q[productType][0].drivers[index].driverUsage = 'excluded';
					}
				})
			}
		}

		q = publicIDTransformer.transform(q, lastResponse);

		var policyHolderhandledRequest = policyHolderHandler.handle(q, userInput);

		return policyHolderhandledRequest;
	}

	function findMatchingQuoteResponse(coverType, offering) {
		var lastQuote = serviceResponseRepository.getLastResponseFrom('quote');
		var lastQuickQuote = serviceResponseRepository.getLastResponseFrom('quickQuote');
		if (lastQuote &&
			coverTypeResolver.resolve(lastQuote) == coverType.descriptor &&
			(!coverOfferingResolver.resolve(lastQuote) || coverOfferingResolver.resolve(lastQuote) == offering.descriptor)
		) {

			if (angular.isObject(lastQuote.vehicles) && lastQuickQuote.length > 0) {
				lastQuote = addDriverFromQuickQuote(lastQuote, 'vehicles');
			}

			if (angular.isObject(lastQuote.caravans) && lastQuickQuote.length > 0) {
				lastQuote = addDriverFromQuickQuote(lastQuote, 'caravans');
			}

			if (angular.isObject(lastQuote.motorcycles) && lastQuickQuote.length > 0) {
				lastQuote = addDriverFromQuickQuote(lastQuote, 'motorcycles');
			}

			return lastQuote;
		}

		return null;
	}

	function transformSaveQuoteRequest() {
		var input = Util.deepClone(userInput);
		structuredRequestTransformer.transform(input);
		var valueSubbedRequest = defaultValueRequestTransformer.transform(null, input);
		var valueReformattedRequest = formatConverterTransformer.transform(valueSubbedRequest);
		var cleanedRequest = cleanRequestTransformer.transform(valueReformattedRequest);
		return cleanedRequest;
	}

	function getCoverTypeSelector(quoteResponse) {
		if (!quoteResponse)
			return null;

		var coverTypeSelector = coverTypeResolver.resolve(quoteResponse);
		var offering = coverOfferingResolver.resolve(quoteResponse);
		if (offering)
			coverTypeSelector += '_' + offering;

		return coverTypeSelector;
	}

	/**
	 * @fullQuoteResponse : full HTTP response including body and headers
	 */
	function updateQuoteCredentials(fullQuoteResponse) {
		var respBody = fullQuoteResponse.data;
		var headers = fullQuoteResponse.headers;
		var quoteRequestCredentials = {};
		/** remove all quote credentials after quote type has changed
												 to not confuse guidewire :) */

		var coverTypeSelector = getCoverTypeSelector(respBody);
		if (coverTypeSelector) {
			quoteRequestCredentials[coverTypeSelector] = {
				quoteID: respBody.quoteID,
				secret: respBody.secret
			}
		}

		var eyUserToken = headers('ey-user-token');
		storage.setItem('eyUserToken', eyUserToken);
		storage.setItem('quoteRequestCredentials', quoteRequestCredentials);
		if (respBody.productCode == "QPMMotorVehicle")
			storage.setItem('selectedCoverType', quoteConfiguration.allCoverTypesByKey[coverTypeSelector]);
	}

	function getSelectedQuoteCredentials() {
		var selectedCoverType = quoteOptions.getSelectedCoverType();
		var quoteRequestCredentials = storage.getItem('quoteRequestCredentials');
		var creds;
		if (selectedCoverType) {
			var coverTypeSelector = quoteOptions.getCoverTypeSelector();
			creds = quoteRequestCredentials[coverTypeSelector];
			var eyUserToken = storage.getItem('eyUserToken');
			if (creds && eyUserToken)
				creds.eyUserToken = eyUserToken;
		}

		return creds;
	}

	function clearQuoteCredentials() {
		storage.setItem('eyUserToken', null);
		storage.setItem('quoteRequestCredentials', {});
	}

	/*
	 * Function - adds new driver from quick quote before submitting to public id transformer
	 * Params - quote, product type
	 * Return - quote
	 */
	function addDriverFromQuickQuote(lastQuote, productType) {
		var driverType = productType == 'motorcycles' ? 'riders' : 'drivers';
		var lastQuick = serviceResponseRepository.getLastResponseFrom('quickQuote');
		var quickQuoteLength = lastQuick[0].response[productType][0][driverType].length;
		var quoteLength = lastQuote[productType][0][driverType].length;
		var lengthDiff = quickQuoteLength - quoteLength;
		var quickQuoteItems = lastQuick[0].response[productType][0][driverType];
		var quoteItems = lastQuote[productType][0][driverType];

		if (lengthDiff > 0) {
			var startIndex = quickQuoteLength - lengthDiff;
			var driver = productType + 'Driver';
			var policyDriver = productType + 'PolicyDriver';
			var person = productType + 'Person';
			var driverHistory = productType + 'DriverHistory';
			var driverType = productType == 'motorcycles' ? 'rider' : 'driver';
			var historyType = productType == 'motorcycles' ? 'riderHistory' : 'driverHistory';

			for (var i = startIndex; i < quickQuoteLength; i++) {
				quickQuoteItems[i].publicID = forgePublicId(moduleNameMapping[driver]);
				quickQuoteItems[i][driverType].publicID = forgePublicId(moduleNameMapping[policyDriver]);
				quickQuoteItems[i][driverType].person.publicID = forgePublicId(moduleNameMapping[person]);

				if (angular.isDefined(quickQuoteItems[i][driverType][historyType])) {
					var driverHistory = quickQuoteItems[i][driverType][historyType];

					angular.forEach(driverHistory, function (history, index) {
						history.publicID = forgePublicId(moduleNameMapping[driverHistory]);
					});
				}

				quoteItems.push(quickQuoteItems[i]);
			}
		}

		return lastQuote;
	}

	return {
		update: updateQuoteRequest,
		getLastRequest: getLastRequest,
		splitQuotes: splitQuotes,
		getQuoteTriggerRequest: getQuoteTriggerRequest,
		getSaveQuoteRequest: getSaveQuoteRequest,
		updateQuoteCredentials: updateQuoteCredentials,
		getSelectedQuoteCredentials: getSelectedQuoteCredentials,
		clearQuoteCredentials: clearQuoteCredentials,
		getUserInput: getUserInput,
	};
}
